import { useEffect, useMemo, useState } from "react";
import { FaBoxOpen } from "react-icons/fa";
import { Link } from "react-router-dom";
import BreadCrumbComp from "../../components/BreadCrumbComp";
import PaginationComp from "../../components/PaginationComp";
import API from "../../utils/api";
import { filterVisibleCategories } from "../../utils/categoryFilters";
import classes from "./Category.module.css";
import PageSchema from "../../components/PageSchema";

const CATEGORIES_PER_PAGE = 10;
const SKELETON_COUNT = 12;

export default function Category() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await API.post("/categories.php", { action: "fetch" });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      const visible = filterVisibleCategories(data);
      const nonEmpty = visible.filter((cat) => Number(cat.count) > 0);
      setCategories(nonEmpty);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      setCategories([]); // if API fails, show empty
    } finally {
      setLoading(false);
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const paginatedCategories = useMemo(() => {
    const start = (currentPage - 1) * CATEGORIES_PER_PAGE;
    return categories.slice(start, start + CATEGORIES_PER_PAGE);
  }, [categories, currentPage]);

  return (
    <>
      <PageSchema
        type="CollectionPage"
        title="Product Categories - Prime Cloud Systems | Servers, PCs & Networking"
        description="Browse all IT hardware categories at Prime Cloud Systems. Find high-quality servers, PCs, laptops, networking equipment, and accessories for home users, IT professionals, and businesses."
        url="https://primecloudsystems.com/category"
        breadcrumbs={[
          { name: "Home", url: "https://primecloudsystems.com/" },
          { name: "Categories", url: "https://primecloudsystems.com/category" },
        ]}
      />

      <BreadCrumbComp
        currentPage="Categories"
        title="Prime Cloud Systems Categories"
        description="Explore Prime Cloud Systems’ wide range of IT hardware, networking equipment, and accessories by category. Discover servers, desktops, laptops, storage devices, and more to meet your technology needs."
      />

      <div className={classes.categoryWrapper}>
        {loading ? (
          <div className={classes.categoryGrid}>
            {Array.from({ length: SKELETON_COUNT }).map((_, idx) => (
              <div key={idx} className={classes.categoryCardSkeleton}>
                <div className={classes.iconSkeleton}></div>
                <div className={classes.nameSkeleton}></div>
                <div className={classes.countSkeleton}></div>
              </div>
            ))}
          </div>
        ) : categories.length === 0 ? (
          <div className={classes.noCategories}>No categories found.</div>
        ) : (
          <>
            <div className={classes.categoryGrid}>
              {paginatedCategories.map((cat) => (
                <Link
                  to={
                    cat.category_slug
                      ? `/category/${cat.category_slug}`
                      : "/shop"
                  }
                  key={cat.id}
                  className={classes.categoryCard}
                >
                  <div className={classes.iconWrapper}>
                    <FaBoxOpen size={40} color="var(--quaternary-color)" />
                  </div>
                  <h3 className={classes.categoryName}>{cat.categoryName}</h3>
                  <p className={classes.categoryCount}>{cat.count} products</p>
                </Link>
              ))}
            </div>

            <PaginationComp
              total={categories.length}
              perPage={CATEGORIES_PER_PAGE}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </>
  );
}
